// --- START OF FILE InteractiveTimelineView.js ---

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Clock, Target, Plus, Trash2, CalendarPlus, ChevronDown, ChevronRight, RefreshCw, Flag } from 'lucide-react';
import GlobalCurrentDayLine from './GlobalCurrentDayLine';
import { buildDefaultSprintPayload } from '../utils/sprintDefaults';

const MS_PER_DAY = 1000 * 60 * 60 * 24;

const parseTimelineDate = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const parts = String(value).split("-").map(Number);
  if (parts.length >= 3 && parts.slice(0, 3).every((part) => Number.isFinite(part))) {
    return new Date(parts[0], parts[1] - 1, parts[2]);
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

const toTimelineDateKey = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;
};

const cloneDay = (date) =>
  date instanceof Date && !Number.isNaN(date.getTime())
    ? new Date(date.getFullYear(), date.getMonth(), date.getDate())
    : null;

const addDays = (date, days) => {
  const next = cloneDay(date);
  if (!next) return null;
  next.setDate(next.getDate() + days);
  return next;
};

const diffDays = (left, right) => {
  const leftDate = cloneDay(left);
  const rightDate = cloneDay(right);
  if (!leftDate || !rightDate) return 0;
  return Math.round((leftDate - rightDate) / MS_PER_DAY);
};

const isMilestoneItem = (item) =>
  item?.__roadmapType === "milestone" || item?.is_milestone === true;

const getFirstMondayInRange = (rangeStart, rangeEnd) => {
  const start = cloneDay(rangeStart);
  const end = cloneDay(rangeEnd);
  if (!start || !end) return null;
  const daysToMonday = (8 - start.getDay()) % 7;
  const monday = addDays(start, daysToMonday);
  return monday && monday <= end ? monday : start;
};

const getLastSaturdayInRange = (rangeStart, rangeEnd) => {
  const start = cloneDay(rangeStart);
  const end = cloneDay(rangeEnd);
  if (!start || !end) return null;
  const daysSinceSaturday = (end.getDay() + 1) % 7;
  const saturday = addDays(end, -daysSinceSaturday);
  return saturday && saturday >= start ? saturday : end;
};

const InteractiveTimelineView = ({ 
  iterations, 
  slots = [], // Ajout des slots à la timeline
  events = [],
  timelineStartDate = null,
  timelineEndDate = null,
  title = "Roadmap",
  subtitle = null,
  variant = "default",
  onIterationUpdate, 
  onSprintUpdate,
  setIterations,
  onIterationAdd = null, // Pour ajouter des itérations
  onSprintAdd = null, // Pour ajouter des sprints
  onIterationDelete = null, // Pour supprimer des itérations
  onSprintDelete = null, // Pour supprimer des sprints
  onIterationSelect = null, // Sélectionner une itération
  onSprintSelect = null, // Sélectionner un sprint
  onIterationReset = null, // Revenir aux dates d'origine
  showIterationToggle = true,
  showSprints = true,
  showProjectLabel = true,
  iterationRowHeight = 48,
  laneGap = 0,
}) => {
  const [dragging, setDragging] = useState(null);
  const [resizing, setResizing] = useState(null);
  const [tempChanges, setTempChanges] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [openIterations, setOpenIterations] = useState({});
  const initRef = useRef(false);
  const dragMovedRef = useRef(false);

  useEffect(() => {
    if (!initRef.current && iterations.length > 0) {
      const init = {};
      const today = new Date();
      iterations.forEach(it => {
        const start = new Date(it.start_date);
        const end = new Date(it.end_date);
        init[it.id] = start <= today && today <= end;
      });
      setOpenIterations(init);
      initRef.current = true;
    }
  }, [iterations]);

  const iterationColors = [
    'bg-blue-200 border-blue-400', 'bg-green-200 border-green-400',
    'bg-yellow-200 border-yellow-400', 'bg-pink-200 border-pink-400',
    'bg-purple-200 border-purple-400'
  ];
  const roadmapIterationTones = [
    { bg: "#dbeafe", border: "#2563eb", text: "#172554", accent: "#1d4ed8" },
    { bg: "#dcfce7", border: "#16a34a", text: "#052e16", accent: "#15803d" },
    { bg: "#fef3c7", border: "#d97706", text: "#451a03", accent: "#b45309" },
    { bg: "#fce7f3", border: "#db2777", text: "#500724", accent: "#be185d" },
    { bg: "#ede9fe", border: "#7c3aed", text: "#2e1065", accent: "#6d28d9" },
    { bg: "#cffafe", border: "#0891b2", text: "#083344", accent: "#0e7490" },
    { bg: "#f1f5f9", border: "#475569", text: "#0f172a", accent: "#334155" },
    { bg: "#ffedd5", border: "#ea580c", text: "#431407", accent: "#c2410c" },
  ];
  const isRoadmapVariant = variant === "roadmap";

  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false, type: null, id: null, iterationId: null, name: ''
  });

  const timelineRef = useRef(null);
  const navigate = useNavigate();

  const eventTypeMeta = {
    employee_arrival: {
      color: "#16a34a",
      label: "Arrivee employe",
      shortLabel: "AE",
    },
    employee_departure: {
      color: "#ef4444",
      label: "Depart employe",
      shortLabel: "DE",
    },
    reinforcement_arrival: {
      color: "#16a34a",
      label: "Arrivee renfort",
      shortLabel: "AR",
    },
    reinforcement_departure: {
      color: "#f59e0b",
      label: "Depart renfort",
      shortLabel: "DR",
    },
  };

  const groupedEvents = useMemo(() => {
    const groups = {};
    (events || []).forEach((event, index) => {
      const rawDate = event?.date instanceof Date ? event.date : new Date(event?.date);
      if (!rawDate || Number.isNaN(rawDate.getTime())) return;
      const date = new Date(rawDate);
      date.setHours(0, 0, 0, 0);
      const key = date.toISOString().split("T")[0];
      if (!groups[key]) groups[key] = [];
      groups[key].push({
        ...event,
        _date: date,
        _key: event.id || `${key}-${index}`,
      });
    });
    return Object.entries(groups)
      .map(([dateKey, items]) => ({ dateKey, items }))
      .sort((a, b) => new Date(a.dateKey) - new Date(b.dateKey));
  }, [events]);

  const iterationTracks = {};
  const trackEndDates = [];
  const hasFixedLanes = iterations.some((iter) => Number.isFinite(iter.laneIndex));
  if (hasFixedLanes) {
    let maxLane = 0;
    iterations.forEach((iter) => {
      const lane = Number.isFinite(iter.laneIndex) ? iter.laneIndex : 0;
      iterationTracks[iter.id] = lane;
      if (lane > maxLane) maxLane = lane;
    });
    trackEndDates.length = maxLane + 1;
  } else {
    [...iterations]
      .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
      .forEach(iter => {
        const start = new Date(iter.start_date);
        let placed = false;
        for (let t = 0; t < trackEndDates.length; t++) {
          if (new Date(trackEndDates[t]) < start) {
            iterationTracks[iter.id] = t;
            trackEndDates[t] = iter.end_date;
            placed = true;
            break;
          }
        }
        if (!placed) {
          iterationTracks[iter.id] = trackEndDates.length;
          trackEndDates.push(iter.end_date);
        }
      });
  }
  const numTracks = trackEndDates.length || 1;

  const ITER_HEIGHT = iterationRowHeight;
  const SPRINT_HEIGHT = 40;
  const SPRINT_MARGIN = 4;
  const sprintStride = SPRINT_HEIGHT + SPRINT_MARGIN;

  const trackHeights = Array(numTracks).fill(0);
  iterations.forEach(iter => {
    const track = iterationTracks[iter.id] || 0;
    const sprintCount = showSprints && openIterations[iter.id] && iter.sprints ? iter.sprints.length : 0;
    const neededHeight = ITER_HEIGHT + sprintCount * sprintStride;
    trackHeights[track] = Math.max(trackHeights[track], neededHeight);
  });
  for (let i = 0; i < numTracks; i++) {
    trackHeights[i] = Math.max(trackHeights[i], ITER_HEIGHT);
  }

  // --- MODIFICATION : Décalage vertical pour les itérations ---
  const ITERATION_TOP_OFFSET = 24; // 1.5rem ou h-6, pour laisser de la place aux noms de slots

  const trackY = [];
  let currentTrackYPosition = ITERATION_TOP_OFFSET; 
  for (let i = 0; i < numTracks; i++) {
    trackY.push(currentTrackYPosition);
    currentTrackYPosition += trackHeights[i] + laneGap;
  }
  const timelineHeight =
    ITERATION_TOP_OFFSET +
    trackHeights.reduce((sum, h) => sum + h, 0) +
    laneGap * Math.max(0, numTracks - 1);
  // --- FIN MODIFICATION ---

  const forcedTimelineStartDate = parseTimelineDate(timelineStartDate);
  const forcedTimelineEndDate = parseTimelineDate(timelineEndDate);

  let timelineViewStartDate;
  if (forcedTimelineStartDate) {
    timelineViewStartDate = forcedTimelineStartDate;
  } else if (iterations.length > 0) {
    const iterationStartDates = iterations.map(iter => new Date(iter.start_date));
    const earliestIterationDate = new Date(Math.min(...iterationStartDates));
    timelineViewStartDate = new Date(earliestIterationDate.getFullYear(), earliestIterationDate.getMonth(), 1);
  } else if (slots.length > 0) {
    const slotStartDates = slots.map(slot => new Date(slot.start_date));
    const earliestSlotDate = new Date(Math.min(...slotStartDates));
    timelineViewStartDate = new Date(earliestSlotDate.getFullYear(), earliestSlotDate.getMonth(), 1);
  } else {
    const today = new Date();
    timelineViewStartDate = new Date(today.getFullYear(), today.getMonth(), 1);
  }

  const allRelevantItemDates = [];
  iterations.forEach(iter => {
    if (iter.start_date) allRelevantItemDates.push(new Date(iter.start_date));
    if (iter.end_date) allRelevantItemDates.push(new Date(iter.end_date));
    (iter.sprints || []).forEach(sprint => {
      if (sprint.start_date) allRelevantItemDates.push(new Date(sprint.start_date));
      if (sprint.end_date) allRelevantItemDates.push(new Date(sprint.end_date));
    });
  });
  slots.forEach(slot => {
    if (slot.start_date) allRelevantItemDates.push(new Date(slot.start_date));
    if (slot.end_date) allRelevantItemDates.push(new Date(slot.end_date));
  });

  let timelineViewEndDate;
  if (forcedTimelineEndDate) {
    timelineViewEndDate = forcedTimelineEndDate;
  } else if (allRelevantItemDates.length > 0) {
    const latestItemDate = new Date(Math.max(...allRelevantItemDates));
    timelineViewEndDate = new Date(latestItemDate.getFullYear(), latestItemDate.getMonth() + 1, 0);
  } else {
    timelineViewEndDate = new Date(timelineViewStartDate.getFullYear(), timelineViewStartDate.getMonth() + 1, 0);
  }
  const minTimelineEndDate = new Date(timelineViewStartDate.getFullYear(), timelineViewStartDate.getMonth() + 1, 0);
  if (timelineViewEndDate < timelineViewStartDate) {
    timelineViewEndDate = new Date(timelineViewStartDate);
  } else if (!forcedTimelineEndDate && timelineViewEndDate < minTimelineEndDate) {
    timelineViewEndDate = minTimelineEndDate;
  }

  const startDate = timelineViewStartDate;
  const endDate = timelineViewEndDate;
  
  const totalDays = Math.max(1, Math.ceil((endDate - startDate) / MS_PER_DAY) + 1);
  const slotSnapTargets = useMemo(() => {
    return (slots || [])
      .map((slot) => {
        const rawStart = parseTimelineDate(slot.start_date);
        const rawEnd = parseTimelineDate(slot.end_date || slot.start_date);
        if (!rawStart || !rawEnd) return null;
        const snapStart = getFirstMondayInRange(rawStart, rawEnd);
        const snapEnd = getLastSaturdayInRange(snapStart || rawStart, rawEnd);
        if (!snapStart || !snapEnd || snapEnd < snapStart) return null;
        return {
          id: slot.id,
          name: slot.name,
          start: snapStart,
          end: snapEnd,
        };
      })
      .filter(Boolean);
  }, [slots]);
  const slotSnapEnabled = isRoadmapVariant && slotSnapTargets.length > 0;
  const getSnapThresholdDays = useCallback((pixelsPerDay) => {
    if (!slotSnapEnabled || !Number.isFinite(pixelsPerDay) || pixelsPerDay <= 0) return 0;
    return Math.max(2, Math.min(7, Math.round(18 / pixelsPerDay)));
  }, [slotSnapEnabled]);
  const findNearestSlotBoundary = useCallback((date, boundary, thresholdDays) => {
    const day = cloneDay(date);
    if (!day || thresholdDays <= 0) return null;
    return slotSnapTargets.reduce((best, target) => {
      const targetDate = target[boundary];
      const distance = Math.abs(diffDays(day, targetDate));
      if (distance > thresholdDays) return best;
      if (!best || distance < best.distance) {
        return { target, date: targetDate, distance };
      }
      return best;
    }, null);
  }, [slotSnapTargets]);
  const applySlotSnapToMove = useCallback((rawStart, rawEnd, pixelsPerDay) => {
    if (!slotSnapEnabled) return { start: rawStart, end: rawEnd };
    const thresholdDays = getSnapThresholdDays(pixelsPerDay);
    const startSnap = findNearestSlotBoundary(rawStart, "start", thresholdDays);
    const endSnap = findNearestSlotBoundary(rawEnd, "end", thresholdDays);
    const durationDays = diffDays(rawEnd, rawStart);

    if (startSnap && endSnap && startSnap.target.id === endSnap.target.id) {
      return { start: cloneDay(startSnap.target.start), end: cloneDay(startSnap.target.end) };
    }
    if (startSnap) {
      if (Math.abs(diffDays(rawEnd, startSnap.target.end)) <= thresholdDays) {
        return { start: cloneDay(startSnap.target.start), end: cloneDay(startSnap.target.end) };
      }
      return {
        start: cloneDay(startSnap.target.start),
        end: addDays(startSnap.target.start, durationDays),
      };
    }
    if (endSnap) {
      return {
        start: addDays(endSnap.target.end, -durationDays),
        end: cloneDay(endSnap.target.end),
      };
    }
    return { start: rawStart, end: rawEnd };
  }, [findNearestSlotBoundary, getSnapThresholdDays, slotSnapEnabled]);
  const applySlotSnapToResize = useCallback((rawDate, edge, pixelsPerDay) => {
    if (!slotSnapEnabled) return rawDate;
    const thresholdDays = getSnapThresholdDays(pixelsPerDay);
    const boundary = edge === "start" ? "start" : "end";
    const snap = findNearestSlotBoundary(rawDate, boundary, thresholdDays);
    return snap ? cloneDay(snap.date) : rawDate;
  }, [findNearestSlotBoundary, getSnapThresholdDays, slotSnapEnabled]);
  
  const totalMonths = Math.ceil(totalDays / 30.44);
  const [displayUnit, setDisplayUnit] = useState(totalMonths > 3 ? 'month' : 'week');
  
  const toggleDisplayUnit = () => {
    setDisplayUnit(displayUnit === 'week' ? 'month' : 'week');
  };

  const handleMouseDown = (e, item, type, action = 'move') => {
    e.preventDefault();
    dragMovedRef.current = false;
    const rect = timelineRef.current.getBoundingClientRect();
    const startX = e.clientX - rect.left;
    if (action === 'resize') {
      e.stopPropagation();
      setResizing({
        type,
        item,
        edge: e.target.dataset.edge,
        startX,
        originalStart: parseTimelineDate(item.start_date),
        originalEnd: parseTimelineDate(item.end_date)
      });
    } else {
      setDragging({
        type,
        item,
        startX,
        originalStart: parseTimelineDate(item.start_date),
        originalEnd: parseTimelineDate(item.end_date)
      });
    }
  };

  const handleMouseMove = useCallback((e) => {
    if (!dragging && !resizing) return;
    dragMovedRef.current = true;
    const rect = timelineRef.current.getBoundingClientRect();
    const currentX = e.clientX - rect.left;
    const pixelsPerDay = rect.width / totalDays;

    if (resizing) {
      const { type, item, edge, originalStart, originalEnd } = resizing;
      const daysDiff = Math.round((currentX - resizing.startX) / pixelsPerDay);
      const updatedItem = { ...item };
      if (edge === 'start') {
        let newStart = new Date(originalStart); newStart.setDate(newStart.getDate() + daysDiff);
        newStart = applySlotSnapToResize(newStart, edge, pixelsPerDay);
        const endBoundary = parseTimelineDate(item.end_date);
        if (endBoundary && newStart >= endBoundary) { newStart = addDays(endBoundary, -1); }
        updatedItem.start_date = toTimelineDateKey(newStart);
      } else {
        let newEnd = new Date(originalEnd); newEnd.setDate(newEnd.getDate() + daysDiff);
        newEnd = applySlotSnapToResize(newEnd, edge, pixelsPerDay);
        const startBoundary = parseTimelineDate(item.start_date);
        if (startBoundary && newEnd <= startBoundary) { newEnd = addDays(startBoundary, 1); }
        updatedItem.end_date = toTimelineDateKey(newEnd);
      }
  
      setTempChanges({
        type,
        id: item.id,
        iteration_id: type === 'sprint' ? item.iteration_id : undefined,
        changes: updatedItem
      });
  
      // Mise à jour visuelle immédiate
      setIterations(prevIterations => 
        prevIterations.map(iteration => {
          if (type === 'iteration' && iteration.id === item.id) {
            return { ...iteration, ...updatedItem };
          }
          if (type === 'sprint') {
            return {
              ...iteration,
              sprints: iteration.sprints?.map(sprint =>
                sprint.id === item.id ? { ...sprint, ...updatedItem } : sprint
              )
            };
          }
          return iteration;
        })
      );
    }
    if (dragging) {
      const { type, item, originalStart, originalEnd } = dragging;
      const daysDiff = Math.round((currentX - dragging.startX) / pixelsPerDay);
      const durationDays = diffDays(originalEnd, originalStart);
      let newStart = new Date(originalStart);
      newStart.setDate(newStart.getDate() + daysDiff);
      let newEnd = addDays(newStart, durationDays);
      const snappedRange = applySlotSnapToMove(newStart, newEnd, pixelsPerDay);
      newStart = snappedRange.start;
      newEnd = snappedRange.end;
  
      const updatedItem = {
        start_date: toTimelineDateKey(newStart),
        end_date: toTimelineDateKey(newEnd)
      };
      if (type === 'iteration' && isMilestoneItem(item)) {
        updatedItem.milestone_date = updatedItem.start_date;
        updatedItem.end_date = updatedItem.start_date;
      }
  
      setTempChanges({
        type,
        id: item.id,
        iteration_id: type === 'sprint' ? item.iteration_id : undefined,
        changes: updatedItem
      });
  
      // Mise à jour visuelle immédiate
      setIterations(prevIterations => 
        prevIterations.map(iteration => {
          if (type === 'iteration' && iteration.id === item.id) {
            return { ...iteration, ...updatedItem };
          }
          if (type === 'sprint') {
            return {
              ...iteration,
              sprints: iteration.sprints?.map(sprint =>
                sprint.id === item.id ? { ...sprint, ...updatedItem } : sprint
              )
            };
          }
          return iteration;
        })
      );
    }
  }, [applySlotSnapToMove, applySlotSnapToResize, dragging, resizing, setIterations, totalDays]);
  
  const handleMouseUp = useCallback(async () => {
    if (tempChanges) {
      try {
        const finalChanges = { ...tempChanges };
        setDragging(null); setResizing(null); setTempChanges(null);
        if (finalChanges.type === 'iteration') {
          await onIterationUpdate(finalChanges.id, finalChanges.changes);
        } else {
          await onSprintUpdate({ id: finalChanges.id, changes: finalChanges.changes }, finalChanges.iteration_id);
        }
      } catch (error) { setIterations(iterations); console.error('Failed to update:', error); }
    } else {
      setDragging(null); setResizing(null);
    }
  }, [iterations, onIterationUpdate, onSprintUpdate, setIterations, tempChanges]);

  useEffect(() => {
    if (dragging || resizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragging, resizing, tempChanges, handleMouseMove, handleMouseUp]);
  
  const months = [];
  let currentDateIterForMonths = new Date(startDate);
  while (currentDateIterForMonths <= endDate) {
    months.push(new Date(currentDateIterForMonths));
    currentDateIterForMonths.setMonth(currentDateIterForMonths.getMonth() + 1);
  }

  const getWeekNumber = (date) => {
    const d = new Date(date); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() + 4 - (d.getDay() || 7));
    const yearStart = new Date(d.getFullYear(), 0, 1);
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  const getTimelineStyles = (itemStartStr, itemEndStr, type = 'iteration') => {
    const startTime = new Date(itemStartStr); startTime.setHours(0, 0, 0, 0);
    const endTime = new Date(itemEndStr); endTime.setHours(0, 0, 0, 0);
    const timelineStartRef = new Date(startDate); timelineStartRef.setHours(0, 0, 0, 0);
    const MS_PER_DAY = 1000 * 60 * 60 * 24;
    
    let extraClasses = '';
    if (type === 'sprint') {
      extraClasses = 'bg-indigo-50 border-indigo-200';
    } else if (type === 'iteration') {
      extraClasses = 'bg-blue-100 border-blue-300';
    } else if (type === 'slot') {
      // Style pour les slots (fond plus discret)
      extraClasses = 'bg-purple-50 border-purple-200 opacity-70'; 
    }
    
    // Calcul de la position
    // Assurer que l'item est visible seulement s'il est dans la plage de la timeline
    const effectiveStart = new Date(Math.max(startTime, timelineStartRef));
    const effectiveEnd = new Date(Math.min(endTime, endDate));
    if (effectiveEnd < effectiveStart) return { left: '0%', width: '0%', transform: 'translateX(-0.5px)', extraClasses };
    const left = ((effectiveStart - timelineStartRef) / MS_PER_DAY) / totalDays * 100;
    const width = ((effectiveEnd - effectiveStart + MS_PER_DAY) / MS_PER_DAY) / totalDays * 100; // +MS_PER_DAY pour inclure le dernier jour
    
    return { 
      left: `${Math.max(0, left)}%`, // S'assurer que left n'est pas négatif
      width: `${Math.max(0, width)}%`, // S'assurer que width n'est pas négatif
      transform: 'translateX(-0.5px)',
      extraClasses
    };
  };

  const getDateLeftPercent = (dateValue) => {
    const itemDate = parseTimelineDate(dateValue);
    if (!itemDate) return null;
    const timelineStartRef = cloneDay(startDate);
    if (!timelineStartRef || itemDate < startDate || itemDate > endDate) return null;
    const left = (diffDays(itemDate, timelineStartRef) / totalDays) * 100;
    return `${Math.max(0, Math.min(100, left))}%`;
  };

  const getTimelineDivisions = () => {
    if (slots && slots.length > 0 && displayUnit !== 'week' && displayUnit !== 'month') {
      return slots.map(slot => ({ id: slot.id, date: new Date(slot.start_date), label: slot.name, type: 'slot' }));
    }
    if (displayUnit === 'week') {
      const weeks = []; let curDate = new Date(startDate); curDate.setHours(0, 0, 0, 0);
      const dayOfWeek = curDate.getDay(); const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      curDate.setDate(curDate.getDate() + diffToMonday);
      while (curDate <= endDate) {
        weeks.push({ date: new Date(curDate), label: `S${getWeekNumber(curDate)}`, type: 'week' });
        curDate.setDate(curDate.getDate() + 7);
      }
      return weeks;
    } else {
      return months.map(date => ({ date: new Date(date), label: date.toLocaleDateString('fr-FR', { month: 'short' }), type: 'month' }));
    }
  };

  const timelineDivisions = getTimelineDivisions();

  // Fonction pour calculer le pourcentage de progression
  const getCompletionPercentage = (sprint) => {
    if (!sprint.total_tokens || sprint.total_tokens === 0) return 0;
    return (sprint.tokens_completed / sprint.total_tokens) * 100;
  };

  // Fonction pour gérer la confirmation de suppression
  const handleDeleteConfirmation = (type, id, iterationId, name) => {
    setDeleteConfirmation({
      isOpen: true,
      type,
      id,
      iterationId,
      name
    });
  };

  // Fonction pour exécuter la suppression
  const executeDelete = () => {
    const { type, id, iterationId } = deleteConfirmation;
    
    if (type === 'iteration' && onIterationDelete) {
      onIterationDelete(id);
    } else if (type === 'sprint' && onSprintDelete) {
      onSprintDelete(id, iterationId);
    }
    
    setDeleteConfirmation({
      isOpen: false,
      type: null,
      id: null,
      iterationId: null,
      name: ''
    });
  };

  const ConfirmationDialog = () => {
    if (!deleteConfirmation.isOpen) return null;
    const itemType = deleteConfirmation.type === 'iteration' ? 'l\'itération' : 'le sprint';
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white p-4 rounded-lg shadow-lg max-w-md w-full">
          <h3 className="text-lg font-medium mb-2">Confirmation de suppression</h3>
          <p>Êtes-vous sûr de vouloir supprimer {itemType} "{deleteConfirmation.name}" ?</p>
          <div className="mt-4 flex justify-end space-x-3">
            <button 
              onClick={() => setDeleteConfirmation({...deleteConfirmation, isOpen: false})}
              className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded"
            >
              Annuler
            </button>
            <button 
              onClick={executeDelete}
              className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded"
            >
              Supprimer
            </button>
          </div>
        </div>
      </div>
    );
  };

  const getIterationVisualMeta = (item) => {
    if (isMilestoneItem(item)) {
      return {
        badgeLabel: "Jalon",
        tooltipLabel: "Jalon objectif",
        overlayStyle: {},
      };
    }

    const isScenarioAddition = item?.__source === "addition";
    const isForecastOnly = item?.is_ca_forecast_overridden !== true;

    if (isScenarioAddition) {
      return {
        badgeLabel: "Scenario",
        tooltipLabel: "Ajout du scenario",
        overlayStyle: {
          borderStyle: "dashed",
          borderWidth: "2px",
          backgroundImage:
            "repeating-linear-gradient(135deg, rgba(15,23,42,0.16) 0, rgba(15,23,42,0.16) 4px, rgba(255,255,255,0) 4px, rgba(255,255,255,0) 10px)",
          backgroundSize: "10px 10px",
          boxShadow: "inset 0 0 0 1px rgba(15,23,42,0.05)",
        },
      };
    }

    if (isForecastOnly) {
      return {
        badgeLabel: "Prev.",
        tooltipLabel: "Iteration previsionnelle",
        overlayStyle: {
          backgroundImage:
            "repeating-linear-gradient(45deg, rgba(255,255,255,0.6) 0, rgba(255,255,255,0.6) 6px, rgba(255,255,255,0) 6px, rgba(255,255,255,0) 12px)",
          backgroundSize: "12px 12px",
        },
      };
    }

    return {
      badgeLabel: null,
      tooltipLabel: "Iteration chiffree",
      overlayStyle: {},
    };
  };
  
  const Tooltip = ({ item, x, y }) => {
    if (!item) return null;
    const isMilestone = isMilestoneItem(item);
    const isIteration = Array.isArray(item.sprints);
    let content;
    if (isMilestone) {
      const projectLabel = item.project_label || item.project_name || item.project || "Projet";
      const milestoneDate = parseTimelineDate(item.milestone_date || item.start_date);
      content = (
        <div>
          <div className="font-semibold text-sm">{item.name || "Jalon"}</div>
          <div className="mt-1 text-xs text-slate-200">{projectLabel}</div>
          <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-white/10 px-2 py-1 text-[11px] font-medium text-slate-100">
            <Flag className="h-3 w-3" />
            Jalon objectif
          </div>
          {milestoneDate && (
            <div className="mt-2 text-xs">{milestoneDate.toLocaleDateString("fr-FR")}</div>
          )}
        </div>
      );
    } else if (isIteration) {
      const projectLabel = item.project_label || item.project_name || item.project || item.name;
      const iterationLabel = item.iteration_label || item.iteration_name || item.subtitle || item.description;
      const caForecast = Number(item.forecast_ca_eur || 0);
      const caActual = item.is_ca_forecast_overridden ? Number(item.valeur || 0) : null;
      const purchaseActual =
        item.budget_purchase_eur !== undefined && item.budget_purchase_eur !== null
          ? Number(item.budget_purchase_eur || 0)
          : Number(item.forecast_purchase_eur || 0);
      const purchaseForecast = Number(item.forecast_purchase_eur || 0);
      const chargeRate = 6.4 / 1000;
      const chargeActual =
        caActual !== null ? Math.max(0, (caActual - purchaseActual) * chargeRate) : null;
      const chargeForecast = Math.max(0, (caForecast - purchaseForecast) * chargeRate);
      const totalTokens = item.sprints.reduce((sum, sp) => sum + (sp.total_tokens || 0), 0);
      const doneTokens = item.sprints.reduce((sum, sp) => sum + (sp.tokens_completed || 0), 0);
      const pct = totalTokens > 0 ? Math.round(doneTokens / totalTokens * 100) : 0;
      const visualMeta = getIterationVisualMeta(item);
      content = (
        <div>
          <div className="font-semibold text-sm">{projectLabel}</div>
          {iterationLabel && (
            <div className="mt-1 text-xs text-slate-200">
              {iterationLabel}
            </div>
          )}
          <div className="mt-2 inline-flex rounded-full bg-white/10 px-2 py-1 text-[11px] font-medium text-slate-100">
            {visualMeta.tooltipLabel}
          </div>
          <div className="mt-2 text-xs">
            {new Date(item.start_date).toLocaleDateString()} - {new Date(item.end_date).toLocaleDateString()}
          </div>
          <div className="mt-2 text-xs space-y-1">
            {caActual !== null && (
              <div>CA reel: {caActual.toLocaleString("fr-FR")} EUR</div>
            )}
            <div>CA prev.: {caForecast.toLocaleString("fr-FR")} EUR</div>
            {chargeActual !== null && (
              <div>Charge reelle: {chargeActual.toLocaleString("fr-FR")} h</div>
            )}
            <div>Charge prev.: {chargeForecast.toLocaleString("fr-FR")} h</div>
          </div>
          {totalTokens > 0 && (
            <div className="flex items-center mt-1 text-xs">
              <Target className="h-3 w-3 mr-1 text-white" />
              <span>{doneTokens}/{totalTokens} tokens ({pct}%)</span>
            </div>
          )}
        </div>
      );
    } else {
      const pct = Math.round(getCompletionPercentage(item));
      content = (
        <div>
          <div className="font-medium text-sm">{item.name}</div>
          {item.vision && (
            <div className="mt-1 text-base font-semibold text-white">
              {item.vision}
            </div>
          )}
          <div className="mt-2 text-xs">
            {new Date(item.start_date).toLocaleDateString()} - {new Date(item.end_date).toLocaleDateString()}
          </div>
          <div className="flex items-center mt-1 text-xs">
            <Target className="h-3 w-3 mr-1 text-white" />
            <span>{item.tokens_completed}/{item.total_tokens} tokens ({pct}%)</span>
          </div>
        </div>
      );
    }
    return ReactDOM.createPortal(
      <div
        className="pointer-events-none bg-gray-800 text-white p-3 rounded shadow-lg z-50 text-xs whitespace-normal max-w-none w-max"
        style={{
          position: 'fixed',
          left: x,
          top: y,
          transform: 'translate(-50%, -100%) translateY(-8px)'
        }}
      >
        {content}
      </div>,
      document.body
    );
  };

 return (
  <div
    className={`h-full border border-slate-200 bg-white shadow-sm ${
      isRoadmapVariant ? "rounded-xl p-3" : "rounded-2xl p-5"
    }`}
  >
    <ConfirmationDialog />
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      <div>
        <h2
          className={`flex items-center font-semibold text-slate-900 ${
            isRoadmapVariant ? "text-base" : "text-xl"
          }`}
        >
          <Clock className="mr-2 h-5 w-5 text-slate-500" />
          {title}
        </h2>
        {subtitle && <p className="mt-1 text-xs text-slate-500">{subtitle}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {/* Bouton pour basculer l'unité d'affichage */}
        <button 
          onClick={toggleDisplayUnit}
          className={
            isRoadmapVariant
              ? "rounded-full border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:border-slate-300 hover:bg-slate-50"
              : "rounded-full bg-blue-50 px-3 py-1.5 text-sm text-blue-700 hover:bg-blue-100"
          }
        >
          {isRoadmapVariant
            ? displayUnit === 'week' ? 'Mois' : 'Semaines'
            : `Afficher par ${displayUnit === 'week' ? 'mois' : 'semaine'}`}
        </button>
        
        {/* Bouton pour ajouter une itération directement depuis la timeline */}
        {onIterationAdd && (
          <button 
            onClick={onIterationAdd}
            className={
              isRoadmapVariant
                ? "flex items-center rounded-full bg-emerald-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-emerald-700"
                : "flex items-center rounded-full bg-green-50 px-3 py-1.5 text-sm text-green-700 hover:bg-green-100"
            }
          >
            <CalendarPlus className="h-4 w-4 mr-1" />
            {isRoadmapVariant ? "Ajouter" : "Nouvelle itération"}
          </button>
        )}
      </div>
    </div>

    <div className="relative select-none">
      {/* L'en-tête des mois longs a été SUPPRIMÉ ici */}
      <div className="relative">
        <div className="sticky top-0 z-20 bg-white">
          <div className="flex border-b border-slate-200">
            {timelineDivisions.map((division, index) => {
              const divisionStart = division.date;
              const timelineEndExclusive = new Date(endDate);
              timelineEndExclusive.setDate(timelineEndExclusive.getDate() + 1);
              let divisionEnd = index < timelineDivisions.length - 1 ? timelineDivisions[index + 1].date : timelineEndExclusive;
              const divisionWidth = ((divisionEnd - divisionStart) / MS_PER_DAY / totalDays) * 100;
              if (divisionWidth <= 0) return null;
              return (
                <div key={`division-label-${index}`} className="relative" style={{ width: `${divisionWidth}%` }}>
                  {division.type === 'slot' ? (
                    <button onClick={() => navigate(`/slot/${division.id}`)} className="w-full pb-2 text-center text-xs text-blue-600 hover:underline" title={division.label}><span className="block truncate">{division.label}</span></button>
                  ) : (
                    <div className="pb-2 text-center text-xs text-slate-500">{division.label}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div ref={timelineRef} className="relative mt-4" style={{ height: `${timelineHeight}px`, minHeight: "100%" }}>
          <div className="absolute w-full pointer-events-none" style={{ top: '-2.25rem', height: 'calc(100% + 2.25rem)' }}> {/* Ajustement du top et height */}
            {timelineDivisions.map((division, index) => {
              const position = ((division.date - startDate) / (1000 * 60 * 60 * 24)) / totalDays * 100;
              if (position >= 0 && position <= 100) {
                return <div key={`division-grid-line-${index}`} className="absolute h-full border-l border-gray-200" style={{ left: `${position}%` }} />;
              }
              return null;
            })}
          </div>
          
          <div className="absolute w-full h-full pointer-events-none">
            {slots.map((slot, index) => {
              const slotStyle = getTimelineStyles(slot.start_date, slot.end_date, 'slot');
              if (parseFloat(slotStyle.width) > 0) {
                return (
                  <div key={`slot-bg-${index}`} className={`absolute h-full border border-dashed ${slotStyle.extraClasses}`} style={{ left: slotStyle.left, width: slotStyle.width, transform: slotStyle.transform, zIndex: 5 }}>
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 text-xs text-purple-500 font-medium pt-1 overflow-hidden whitespace-nowrap opacity-80 pointer-events-none">{slot.name}</div>
                  </div>
                );
              }
              return null;
            })}
          </div>

          {groupedEvents.length > 0 && (
            <div className="absolute w-full h-full pointer-events-none" style={{ zIndex: 7 }}>
              {groupedEvents.map((group) => {
                const eventDate = group.items[0]._date;
                if (eventDate < startDate || eventDate > endDate) return null;
                const timelineStartRef = new Date(startDate);
                timelineStartRef.setHours(0, 0, 0, 0);
                const left = ((eventDate - timelineStartRef) / MS_PER_DAY) / totalDays * 100;
                const primaryMeta =
                  eventTypeMeta[group.items[0].type] || {
                    color: "#0f172a",
                    label: "Evenement",
                    shortLabel: "EV",
                  };
                return (
                  <div
                    key={`event-group-${group.dateKey}`}
                    className="absolute top-0 bottom-0"
                    style={{ left: `${left}%` }}
                  >
                    <div
                      className="absolute top-0 bottom-0 border-l border-dashed"
                      style={{ borderColor: primaryMeta.color, opacity: 0.6 }}
                    />
                    {group.items.map((event, index) => {
                      const meta =
                        eventTypeMeta[event.type] || {
                          color: "#0f172a",
                          label: "Evenement",
                          shortLabel: "EV",
                        };
                      const dateLabel = eventDate.toLocaleDateString("fr-FR");
                      const title = `${meta.label}: ${event.name || "Sans nom"} (${dateLabel})`;
                      return (
                        <div
                          key={`event-${event._key}`}
                          className="absolute"
                          style={{ top: `${4 + index * 12}px`, transform: "translateX(-50%)" }}
                          title={title}
                        >
                          <span
                            className="inline-flex items-center justify-center rounded border bg-white px-1 text-[10px] font-medium"
                            style={{ color: meta.color, borderColor: meta.color }}
                          >
                            {meta.shortLabel}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}

          <GlobalCurrentDayLine startDate={startDate} endDate={endDate} />
          {iterations.map((iteration, index) => {
            const trackIndex = iterationTracks[iteration.id] || 0;
            const topY = trackY[trackIndex] || 0; // topY inclut déjà ITERATION_TOP_OFFSET
            const colorIndex = Number.isFinite(iteration.colorIndex) ? iteration.colorIndex : index;
            const colorClasses = iterationColors[colorIndex % iterationColors.length];
            const hueMatchIter = colorClasses.match(/bg-([a-z]+)-\d+/);
            const hueIter = hueMatchIter ? hueMatchIter[1] : 'blue';
            const iterStyle = getTimelineStyles(iteration.start_date, iteration.end_date, 'iteration');
            if (parseFloat(iterStyle.width) <= 0) return null;
            const projectLabel = iteration.project_label || iteration.project_name || iteration.project || iteration.name;
            const iterationLabel = iteration.iteration_label || iteration.iteration_name || iteration.subtitle || iteration.description;
            const mainLabel = showProjectLabel ? projectLabel : (iterationLabel || iteration.name);
            const subLabel = showProjectLabel ? iterationLabel : null;
            const hasBaseDates = iteration.base_start_date && iteration.base_end_date;
            const ghostStyle = hasBaseDates
              ? getTimelineStyles(iteration.base_start_date, iteration.base_end_date, "iteration")
              : null;
            const visualMeta = getIterationVisualMeta(iteration);
            const roadmapTone = roadmapIterationTones[colorIndex % roadmapIterationTones.length];
            const iterationStartBoundary = parseTimelineDate(iteration.start_date);
            const iterationEndBoundary = parseTimelineDate(iteration.end_date);
            const startsBeforeWindow = iterationStartBoundary ? iterationStartBoundary < startDate : false;
            const endsAfterWindow = iterationEndBoundary ? iterationEndBoundary > endDate : false;

            if (isMilestoneItem(iteration)) {
              const markerLeft = getDateLeftPercent(iteration.milestone_date || iteration.start_date);
              if (!markerLeft) return null;
              const milestoneDate = parseTimelineDate(iteration.milestone_date || iteration.start_date);
              const title = `${mainLabel || "Jalon"} - ${
                milestoneDate ? milestoneDate.toLocaleDateString("fr-FR") : ""
              }`;
              return (
                <React.Fragment key={iteration.id}>
                  <div
                    className="absolute top-0 bottom-0 border-l border-dashed border-rose-300"
                    style={{
                      left: markerLeft,
                      zIndex: 8,
                      opacity: 0.7,
                    }}
                  />
                  <button
                    type="button"
                    className="absolute flex max-w-[190px] -translate-x-1/2 cursor-move items-center gap-1.5 rounded-full border border-rose-200 bg-white px-2 py-1.5 text-left text-[11px] font-semibold text-slate-800 shadow-sm ring-1 ring-white/80 transition hover:border-rose-300 hover:bg-rose-50"
                    style={{
                      top: `${topY + 7}px`,
                      left: markerLeft,
                      zIndex: 22,
                    }}
                    title={title}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (dragMovedRef.current) {
                        dragMovedRef.current = false;
                        return;
                      }
                      if (onIterationSelect) onIterationSelect(iteration);
                    }}
                    onMouseEnter={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setHoveredItem({ item: iteration, x: rect.left + rect.width / 2, y: rect.top });
                    }}
                    onMouseLeave={() => setHoveredItem(null)}
                    onMouseDown={(e) => handleMouseDown(e, iteration, 'iteration')}
                  >
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-rose-100 text-rose-600">
                      <Flag className="h-3.5 w-3.5" />
                    </span>
                    <span className="truncate">{mainLabel || "Jalon"}</span>
                  </button>
                </React.Fragment>
              );
            }

            return (
              <React.Fragment key={iteration.id}>
                {hasBaseDates && ghostStyle && parseFloat(ghostStyle.width) > 0 && (
                  <div
                    className="absolute h-12 rounded-lg border border-dashed border-blue-500"
                    style={{
                      top: `${topY}px`,
                      left: ghostStyle.left,
                      width: ghostStyle.width,
                      transform: ghostStyle.transform,
                      zIndex: 12,
                      backgroundColor: "rgba(59, 130, 246, 0.08)",
                      pointerEvents: "none",
                    }}
                  />
                )}
                <div
                  className={`absolute h-12 rounded-lg flex items-center justify-between cursor-move group border ${
                    isRoadmapVariant ? "pl-5 pr-1.5" : "pl-8 pr-2"
                  } ${
                    isRoadmapVariant ? "shadow-sm ring-1 ring-white/70" : colorClasses
                  }`}
                  style={{
                    top: `${topY}px`,
                    left: iterStyle.left,
                    width: iterStyle.width,
                    transform: iterStyle.transform,
                    zIndex: 10,
                    ...(isRoadmapVariant
                      ? {
                          backgroundColor: roadmapTone.bg,
                          borderColor: roadmapTone.border,
                          boxShadow: `0 8px 18px -16px ${roadmapTone.border}`,
                        }
                      : {}),
                    ...visualMeta.overlayStyle,
                  }}
                  title={`${mainLabel || "Iteration"} - ${new Date(iteration.start_date).toLocaleDateString("fr-FR")} au ${new Date(iteration.end_date).toLocaleDateString("fr-FR")}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    if (dragMovedRef.current) {
                      dragMovedRef.current = false;
                      return;
                    }
                    if (onIterationSelect) onIterationSelect(iteration);
                  }}
                  onMouseEnter={(e) => { const rect = e.currentTarget.getBoundingClientRect(); setHoveredItem({ item: iteration, x: rect.left + rect.width / 2, y: rect.top }); }}
                  onMouseLeave={() => setHoveredItem(null)}
                  onMouseDown={(e) => handleMouseDown(e, iteration, 'iteration')}
                >
                  {isRoadmapVariant && (
                    <>
                      <div
                        className="absolute left-0 top-0 h-full w-1.5 rounded-l-lg"
                        style={{ backgroundColor: roadmapTone.accent }}
                      />
                      {startsBeforeWindow && (
                        <div className="absolute left-0 top-0 h-full w-2 rounded-l-lg border-l-2 border-dashed border-slate-900/50" />
                      )}
                      {endsAfterWindow && (
                        <div className="absolute right-0 top-0 h-full w-2 rounded-r-lg border-r-2 border-dashed border-slate-900/50" />
                      )}
                    </>
                  )}
                  <div className={`absolute left-0 top-0 w-2 h-full cursor-ew-resize opacity-0 group-hover:opacity-100 bg-${hueIter}-300`} data-edge="start" onMouseDown={(e) => handleMouseDown(e, iteration, 'iteration', 'resize')} />
                  {showIterationToggle && (
                    <button onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpenIterations({ ...openIterations, [iteration.id]: !openIterations[iteration.id] }); }} className="absolute left-2 top-1/2 transform -translate-y-1/2 text-blue-800 hover:text-blue-600 z-20">
                      {openIterations[iteration.id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                  )}
                  <div className={`flex flex-col flex-grow truncate ${isRoadmapVariant ? "ml-2" : "ml-4"}`}>
                    <div className="flex items-center gap-2">
                      <span
                        className={`truncate ${
                          isRoadmapVariant ? "text-xs font-bold" : "text-sm font-semibold text-blue-900"
                        }`}
                        style={isRoadmapVariant ? { color: roadmapTone.text } : undefined}
                      >
                        {mainLabel}
                      </span>
                      {visualMeta.badgeLabel && (
                        <span className="shrink-0 rounded-full bg-white/80 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600 shadow-sm">
                          {visualMeta.badgeLabel}
                        </span>
                      )}
                    </div>
                    {subLabel && (
                      <span
                        className={`truncate ${isRoadmapVariant ? "text-[11px]" : "text-[11px] text-blue-700/80"}`}
                        style={isRoadmapVariant ? { color: roadmapTone.accent } : undefined}
                      >
                        {subLabel}
                      </span>
                    )}
                  </div>
                  {onIterationReset && hasBaseDates && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onIterationReset(iteration.id);
                      }}
                      className="p-1 text-blue-500 hover:text-blue-700 mr-1 z-20"
                      title="Revenir aux dates d'origine"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </button>
                  )}
                  {onSprintAdd && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        const payload = buildDefaultSprintPayload(iteration);
                        onSprintAdd(iteration.id, payload);
                      }}
                      className="p-1 text-blue-500 hover:text-blue-700 mr-2 z-20"
                      title="Ajouter un sprint"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  )}
                  <div className={`absolute right-0 top-0 w-2 h-full cursor-ew-resize opacity-0 group-hover:opacity-100 bg-${hueIter}-300`} data-edge="end" onMouseDown={(e) => handleMouseDown(e, iteration, 'iteration', 'resize')} />
                </div>
                {showSprints && openIterations[iteration.id] && iteration.sprints?.map((sprint, idx) => {
                  const hueMatch = iterationColors[colorIndex % iterationColors.length].match(/bg-([a-z]+)-\d+/);
                  const hue = hueMatch ? hueMatch[1] : 'gray';
                  const backgroundShades = [100, 200, 300, 400]; const borderShades = backgroundShades.map(s => s + 200);
                  const variantIndex = idx % backgroundShades.length;
                  const bgClass = `bg-${hue}-${backgroundShades[variantIndex]}`; const borderClass = `border-${hue}-${borderShades[variantIndex]}`;
                  const sprintTop = topY + ITER_HEIGHT + sprintStride * idx;
                  const sprintStyle = getTimelineStyles(sprint.start_date, sprint.end_date, 'sprint');
                  if (parseFloat(sprintStyle.width) <= 0) return null;
                  return (
                    <div key={sprint.id} className={`absolute h-10 rounded flex items-center justify-between px-2 cursor-move group border ${bgClass} ${borderClass}`} style={{ top: `${sprintTop}px`, left: sprintStyle.left, width: sprintStyle.width, transform: sprintStyle.transform, zIndex: 15 }}
                      onClick={(e) => { e.stopPropagation(); e.preventDefault(); if (onSprintSelect) onSprintSelect(sprint); }}
                      onMouseEnter={(e) => { const rect = e.currentTarget.getBoundingClientRect(); setHoveredItem({ item: sprint, x: rect.left + rect.width / 2, y: rect.top }); }}
                      onMouseLeave={() => setHoveredItem(null)}
                      onMouseDown={(e) => handleMouseDown(e, { ...sprint, iteration_id: iteration.id }, 'sprint')}
                    >
                      <div className={`absolute inset-0 opacity-75 rounded-l bg-${hue}-300`} style={{ width: `${getCompletionPercentage(sprint)}%` }} />
                      <div className={`absolute left-0 top-0 w-2 h-full cursor-ew-resize opacity-0 group-hover:opacity-100 bg-${hue}-300 z-10`} data-edge="start" onMouseDown={(e) => handleMouseDown(e, { ...sprint, iteration_id: iteration.id }, 'sprint', 'resize')} />
                      <div className="flex flex-col relative z-10 flex-grow truncate space-y-1">
                        <span className="text-xs text-indigo-700 truncate">{sprint.description}</span>
                        {sprint.vision && <span className="text-[10px] text-gray-500 italic truncate">{sprint.vision}</span>}
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleDeleteConfirmation('sprint', sprint.id, iteration.id, sprint.description || sprint.name); }} className="p-1 text-red-600 hover:text-red-800 hover:bg-red-100 rounded opacity-0 group-hover:opacity-100 transition-opacity relative z-10" title="Supprimer le sprint"><Trash2 className="h-3 w-3" /></button>
                      <div className={`absolute right-0 top-0 w-2 h-full cursor-ew-resize opacity-0 group-hover:opacity-100 bg-${hue}-300 z-10`} data-edge="end" onMouseDown={(e) => handleMouseDown(e, { ...sprint, iteration_id: iteration.id }, 'sprint', 'resize')} />
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })}
        </div>
      </div>
      {hoveredItem && <Tooltip item={hoveredItem.item} x={hoveredItem.x} y={hoveredItem.y} />}
    </div>
  </div>
);
};

export default InteractiveTimelineView;
// --- END OF FILE InteractiveTimelineView.js ---
